/**
 * OS CAMPOS DE CADA SEÇÃO DO LAUDO, com o nome que o laboratório usa.
 *
 * As seções são gravadas como JSONB (`identificacao`, `fisico_quimica`,
 * `agd_gases`, `pcb`, `furfuraldeido`), então o banco só guarda a chave técnica
 * (`rigidezDieletrica`). Quem lê o laudo precisa do rótulo do papel
 * ("Rigidez dielétrica — disco (KV)"), e ele estava escrito à mão dentro da
 * tela de preenchimento — invisível para qualquer outra tela.
 *
 * Aqui fica a lista, num lugar só, na ORDEM do formulário: é ela que a tela de
 * visualização (dois cliques em Relatórios) usa para mostrar o laudo inteiro.
 * Campo que aparecer no banco e não estiver nesta lista NÃO é escondido — sai
 * com a própria chave, para nunca sumir informação da vista.
 *
 * O TÍTULO de cada seção vem de `ROTULO_SECAO` (src/config.ts) — o nome do
 * ensaio mora num lugar só, senão a mesma seção se chamaria de um jeito no menu
 * e de outro no laudo (foi exatamente o que acontecia).
 */
import { ROTULO_SECAO } from "./config";

export interface CampoLaudo {
  chave: string;
  label: string;
}

export const CAMPOS_IDENTIFICACAO: CampoLaudo[] = [
  { chave: "numeroAmostra", label: "Nº da coleta / laudo" },
  { chave: "localInstalacao", label: "Local de instalação" },
  { chave: "fabricante", label: "Fabricante" },
  { chave: "numeroSerie", label: "Número de série" },
  { chave: "potenciaKVA", label: "Potência (KVA)" },
  { chave: "dataFabricacao", label: "Data de fabricação" },
  { chave: "impedanciaPercent", label: "Impedância (%)" },
  { chave: "volumeOleoL", label: "Volume de óleo (L)" },
  { chave: "tensaoInferiorV", label: "Tensão inferior (V)" },
  { chave: "tensaoSuperiorKV", label: "Tensão superior (KV)" },
  { chave: "dataColeta", label: "Data de coleta" },
  { chave: "outros", label: "Outros" },
  // Os campos que os modelos de Gases e PCB pedem (server/relatorio-modelos.ts).
  { chave: "motivo", label: "Motivo da coleta" },
  { chave: "cnpj", label: "CNPJ do cliente" },
  { chave: "cidadeUf", label: "Cidade/UF do cliente" },
  { chave: "contato", label: "Contato do cliente" },
  { chave: "tipoEquipamento", label: "Tipo de equipamento" },
  { chave: "tag", label: "Identificação/TAG" },
  { chave: "tipoOleo", label: "Tipo de óleo" },
  { chave: "sequenciaColeta", label: "Sequência de coleta" },
  { chave: "pontoAmostragem", label: "Ponto de amostragem" },
  { chave: "amostrador", label: "Amostrador" },
  { chave: "emOperacao", label: "Em operação" },
  { chave: "temperaturaAmostra", label: "Temp. da amostra (°C)" },
  { chave: "temperaturaAmbiente", label: "Temp. ambiente (°C)" },
  { chave: "umidadeRelativa", label: "Umidade relativa (%)" },
];

export const CAMPOS_FISICO_QUIMICA: CampoLaudo[] = [
  { chave: "aparencia", label: "Aparência" },
  { chave: "cor", label: "Cor" },
  { chave: "densidade20_4", label: "Densidade a 20/4ºC (g/cm³)" },
  { chave: "tensaoInterfacial25", label: "Tensão interfacial a 25ºC (mN/m)" },
  { chave: "teorAgua", label: "Teor de água medido (ppm)" },
  { chave: "indiceNeutralizacao", label: "Índice de neutralização (mgKOH/g)" },
  { chave: "rigidezDieletrica", label: "Rigidez dielétrica — disco (KV)" },
  { chave: "fatorPotencia90", label: "Fator de potência a 90ºC (%)" },
  { chave: "fatorPotencia25", label: "Fator de potência a 25ºC (%)" },
  { chave: "fatorPotencia100", label: "Fator de potência a 100ºC (%)" },
];

export const CAMPOS_AGD_GASES: CampoLaudo[] = [
  { chave: "h2", label: "H2 — Hidrogênio" },
  { chave: "o2", label: "O2 — Oxigênio" },
  { chave: "n2", label: "N2 — Nitrogênio" },
  { chave: "ch4", label: "CH4 — Metano" },
  { chave: "co", label: "CO — Monóxido de Carbono" },
  { chave: "co2", label: "CO2 — Dióxido de Carbono" },
  { chave: "c2h4", label: "C2H4 — Etileno" },
  { chave: "c2h6", label: "C2H6 — Etano" },
  { chave: "c2h2", label: "C2H2 — Acetileno" },
  // Os dois totais são CALCULADOS (src/totais-gases.ts) — a tela os mostra
  // somente leitura. "Próxima amostragem" saiu: o prazo é dado na etapa de
  // diagnóstico (reamostragem pelo enquadramento da NBR 7274).
  { chave: "total", label: "Total de Gases" },
  { chave: "totalCombustiveis", label: "Total de Gases Combustíveis" },
];

export const CAMPOS_PCB: CampoLaudo[] = [
  { chave: "teorPcbClorado", label: "Teor de PCB e Clorado (Mg/kg)" },
  { chave: "dataEnsaio", label: "Data do ensaio" },
];

export const CAMPOS_FURFURALDEIDO: CampoLaudo[] = [
  { chave: "fal2", label: "2-Furfural (2FAL)" },
  { chave: "fol2", label: "2-Furfurilalcool (2FOL)" },
  { chave: "acf2", label: "2-Acetil Furano (2ACF)" },
  { chave: "hmf5", label: "5-Hidroximetil-2-Furfural (5HMF)" },
  { chave: "mef5", label: "5-Metil-2-Furfural (5MEF)" },
  { chave: "grauPolimerizacao", label: "Grau de polimerização (GP)" },
];

/** A seção como ela aparece na tela de visualização, na ordem do laudo. */
export interface SecaoLaudo {
  /** Chave do JSONB no registro que veio do servidor. */
  dados: "identificacao" | "fisico_quimica" | "agd_gases" | "pcb" | "furfuraldeido";
  /** Chave da seção de ANÁLISE (para saber se ela foi solicitada nesta OS). */
  selecao?: "fisicoQuimica" | "agdGases" | "pcb" | "furfuraldeido";
  titulo: string;
  campos: CampoLaudo[];
}

export const SECOES_LAUDO: SecaoLaudo[] = [
  { dados: "identificacao", titulo: "Identificação do equipamento", campos: CAMPOS_IDENTIFICACAO },
  {
    dados: "fisico_quimica",
    selecao: "fisicoQuimica",
    titulo: ROTULO_SECAO.fisicoQuimica!,
    campos: CAMPOS_FISICO_QUIMICA,
  },
  {
    dados: "agd_gases",
    selecao: "agdGases",
    titulo: ROTULO_SECAO.agdGases!,
    campos: CAMPOS_AGD_GASES,
  },
  { dados: "pcb", selecao: "pcb", titulo: ROTULO_SECAO.pcb!, campos: CAMPOS_PCB },
  {
    dados: "furfuraldeido",
    selecao: "furfuraldeido",
    titulo: ROTULO_SECAO.furfuraldeido!,
    campos: CAMPOS_FURFURALDEIDO,
  },
];

/**
 * Os pares "rótulo → valor" de uma seção, na ordem do laudo.
 *
 * O que estiver preenchido no banco mas fora do catálogo entra no fim com a
 * própria chave: informação que existe no laudo NUNCA pode sumir da tela só
 * porque esta lista ficou para trás.
 */
export function paresDaSecao(
  valores: Record<string, unknown> | null | undefined,
  campos: CampoLaudo[],
): { label: string; valor: string }[] {
  const dados = valores && typeof valores === "object" ? (valores as Record<string, unknown>) : {};
  const conhecidos = new Set(campos.map((c) => c.chave));
  const pares = campos.map((c) => ({ label: c.label, valor: String(dados[c.chave] ?? "").trim() }));
  for (const [chave, v] of Object.entries(dados)) {
    if (conhecidos.has(chave)) continue;
    const valor = String(v ?? "").trim();
    if (valor !== "") pares.push({ label: chave, valor });
  }
  return pares;
}
